import React from 'react';

import { useAppSelector } from '@fd/customHooks/useAppSelector';
import useQueryRMSCommonDataHook from '@fd/customHooks/useQueryRMSCommonDataHook';
import * as FlipdishApi from '@flipdish/api-client-typescript';
import {
  Announcement01,
  Award03,
  Bank,
  BankNote02,
  BarChartSquare01,
  Bell01,
  BookClosed,
  BookmarkCheck,
  BookOpen01,
  Building02,
  Building05,
  Building07,
  Calculator,
  Car01,
  CheckVerified03,
  ClockSnooze,
  Code02,
  CoinsHand,
  CreditCard02,
  Data,
  Dataflow02,
  Dataflow03,
  Dotpoints01,
  File06,
  Fingerprint02,
  Globe01,
  GoogleChrome,
  Grid01,
  HomeLine,
  ImageCheck,
  Inbox01,
  List,
  Lock01,
  MessageTextSquare01,
  Monitor01,
  Monitor02,
  Phone02,
  PhoneCall02,
  PlusCircle,
  Rocket02,
  Rows01,
  SearchSm,
  Settings01,
  ShieldTick,
  ShoppingBag01,
  ShoppingCart03,
  Stand,
  Stars01,
  Tablet02,
  Tag03,
  TerminalSquare,
  Tool02,
  TrendUp01,
  Tv01,
  Users01,
  UsersEdit,
  UserSquare,
  Wallet01,
} from '@flipdish/ui-icons';

import { shouldShowRMSMenuEditor } from '../../../components/MenuEditor/utils';
import { getMenuListingPath } from '../../../components/MenusV3/menuListingPath';
import { useHasAccessToOnboardingV3 } from '../../../components/OnboardingV3/hooks/useHasAccessToOnboardingV3';
import { getPropertiesPermissions } from '../../../components/Properties/propertiesPermissions';
import { accountsPermissions } from '../../../components/RMS/Accounts/permissions';
import { inventoryPermissions } from '../../../components/RMS/Inventory/inventoryPermissions';
import { payrollPermissions } from '../../../components/RMS/Payroll/payrollPermissions';
import { MenuSource } from '../../../components/RMS/rms.services';
import {
  franchisorPermissions,
  permissionsIncludingPropertyOwner,
  propertyManagerRolePermission,
  propertyOwnerRolePermission,
} from '../../../components/RMS/utils/permissions';
import { RMSSubscriptions } from '../../../components/RMS/utils/rmsSubscriptions';
import { getSalesChannelPermissions } from '../../../components/SalesChannels/permissions';
import { entitlementConstants } from '../../../constants/entitlement.constants';
import useHasEntitlement from '../../../custom-hooks/useHasEntitlement';
import { PortalFeatureFlags } from '../../../services/featureFlags/portalFeatureFlags';
import { useBooleanFeatureFlagResolution } from '../../../services/featureFlags/useBooleanFeatureFlagResolution';
import { useFeatureFlagReady } from '../../../services/featureFlags/useFeatureFlagReady';
import { useFeatureFlagValue } from '../../../services/featureFlags/useFeatureFlagValue';
import { type FrontEndFlag } from '../../../services/flagService/featureFlagList';
import { flagService } from '../../../services/flagService/flagService';
import { PortalViewId } from '../../../viewConfiguration';
import { getSalesOrdersNavChoice, removeOmittedMenuItems } from './utils/sidebar.utils';

type MenuItemViewFields = {
  viewId?: PortalViewId;
  exemptFromViewConfiguration?: boolean;
};

export type ItemMetaData = {
  hidden?: boolean;
  /**
   * When true, the item is removed from the nav entirely (hard-omit).
   * Unlike `hidden`, Show hidden features does not override this.
   */
  omit?: boolean;
  icon: JSX.Element;
  isBeta?: boolean;
  isMFE?: boolean;
  path: string;
  permissions?: AppState['permissions'];
  title: TranslationId;
  searchAlias?: string[];
  /** Footer groups render below the main list, in config order. */
  section?: 'footer';
  /** Switch the sidebar shows under this group's sub-items; the sidebar hook owns the handler. */
  toggle?: 'showHiddenFeatures';
  subItems?: Array<
    {
      title: TranslationId;
      path: string;
      icon?: React.ReactElement;
      hidden?: boolean;
      /**
       * When true, the item is removed from the nav entirely (hard-omit).
       * Unlike `hidden`, Show hidden features does not override this.
       */
      omit?: boolean;
      isBeta?: boolean;
      permissions?: AppState['permissions'];
      isMFE?: boolean;
      searchAlias?: string[];
    } & MenuItemViewFields
  >;
} & MenuItemViewFields;

const getMenuNavItem = (
  currentAppId: string,
  showMenuEditor: boolean,
  isRestrictedOnboardingNav: boolean,
  menuListingPath: string,
  useAuthZPermissions: boolean,
  showMenuSectionNav: boolean
): ItemMetaData => {
  const getPath = (basePath: string) => basePath;

  if (showMenuEditor && showMenuSectionNav) {
    return {
      title: 'Menu',
      path: '',
      icon: <BookOpen01 />,
      hidden: isRestrictedOnboardingNav,
      permissions: useAuthZPermissions
        ? ['UpdateMenu', 'FlipdishStaff']
        : ['Owner', 'ManagedOwner', 'FlipdishStaff', 'PropertyOwner'],
      subItems: [
        {
          title: 'Menu_Editor',
          path: menuListingPath,
          icon: <Dotpoints01 />,
          isMFE: true,
          permissions: useAuthZPermissions
            ? ['UpdateMenu', 'FlipdishStaff']
            : ['Owner', 'ManagedOwner', 'FlipdishStaff', 'PropertyOwner'],
          searchAlias: [
            'Menu',
            'Edit menu',
            'Update menu',
            'Option set',
            'Menu category',
            'Menu pricing',
            'Menu price',
            'Assign menu',
            'Menu image',
          ],
        },
        {
          title: 'Menu_Actions',
          path: `/${currentAppId}/menu-actions`,
          icon: <ClockSnooze />,
          isMFE: true,
          permissions: useAuthZPermissions
            ? ['UpdateMenu', 'FlipdishStaff']
            : ['Owner', 'ManagedOwner', 'FlipdishStaff', 'PropertyOwner'],
          searchAlias: ['Menu actions', 'Snooze', '86'],
        },
      ],
    };
  }

  if (showMenuEditor) {
    return {
      title: 'Menu_Editor',
      path: menuListingPath,
      icon: <BookOpen01 />,
      hidden: isRestrictedOnboardingNav,
      permissions: useAuthZPermissions
        ? ['UpdateMenu', 'FlipdishStaff']
        : ['Owner', 'ManagedOwner', 'FlipdishStaff', 'PropertyOwner'],
      isMFE: true,
      searchAlias: [
        'Menu',
        'Edit menu',
        'Update menu',
        'Option set',
        'Menu category',
        'Menu pricing',
        'Menu price',
        'Assign menu',
        'Menu image',
      ],
    };
  }

  return {
    title: 'Menus',
    path: getPath(`/${currentAppId}/menus`),
    icon: <BookOpen01 />,
    hidden: isRestrictedOnboardingNav,
    permissions: ['ViewMenu'],
    searchAlias: [
      'Menu',
      'Edit menu',
      'Update menu',
      'Option set',
      'Menu category',
      'Menu pricing',
      'Menu price',
      'Assign menu',
    ],
  };
};

const shouldShowVerifactu = ({
  showVerifactuFlag,
  orgCountryCode,
  orgId,
  isProduction,
}: {
  showVerifactuFlag: boolean;
  orgCountryCode?: string;
  orgId?: string;
  isProduction: boolean;
}): boolean => {
  // Enabled for production Mexican Cafe for Flipdish testing only.
  if (isProduction && orgId === 'org10030') return true;
  // TODO: Remove flag once its 100% released and we are happy with it.
  return showVerifactuFlag && orgCountryCode === 'ES';
};

const useFlag = (flag: FrontEndFlag) => useAppSelector((s) => flagService.isFlagOn(s, flag));

export const useMenuItemsConfig = (
  currentApp: FlipdishApi.App,
  isProductBasedMenusOn: boolean,
  menuSource?: MenuSource,
  subscriptions = Array<RMSSubscriptions>()
): ItemMetaData[] => {
  const { AppId: currentAppId, Features, CountryId } = currentApp;
  const { data } = useQueryRMSCommonDataHook(currentAppId ?? '');
  const isOrgListingV3On = useFeatureFlagValue(PortalFeatureFlags.showMenuListingV3Org, false);
  // Off by default until the org flag is created and enabled in Harness.
  const showMenuSectionNav = useFeatureFlagValue(
    PortalFeatureFlags.menuV3_showMenuSectionNav,
    false
  );
  const isOrgListingV3Ready = useFeatureFlagReady(PortalFeatureFlags.showMenuListingV3Org);
  const { reducedOnboardingView, onboardingWaitingView, shouldShowFirstTimeSetup } =
    useHasAccessToOnboardingV3();
  const isRestrictedOnboardingNav = reducedOnboardingView || onboardingWaitingView;
  const isApiFlagOn = data?.associations?.isMigratedToSalesChannels;
  const currentOrg = useAppSelector((s) => s.rms.currentOrg);
  const orgCountryCode = currentOrg?.countryCode;
  const orgId = currentOrg?.orgId;
  const { hasEntitlement: hasPhoneAgentEntitlement, isLoading: isLoadingEntitlement } =
    useHasEntitlement(orgId, entitlementConstants.PhoneAgent.AiAgent);
  const statePermissions = useAppSelector((s) => s.permissions);
  const permissions = statePermissions ?? [];
  const bankAccountAccessPermissions: AppState['permissions'] = [
    'ViewBankAccounts',
    'FlipdishStaff',
  ];
  const hasBankAccountsAccess = permissions.some((permission) =>
    bankAccountAccessPermissions.includes(permission)
  );
  const isProduction = process.env.ENVIRONMENT === 'production';

  /**
   * If apiFlag is off, override by adding appId to showSalesChannelStructure FF in Split.
   *
   * e.g api says hide it, then add the appId to the FF in Split to show it
   * */
  const showSalesChannelStructureFlag = useFlag('showSalesChannelStructure');
  const showSalesChannelStructure = isApiFlagOn || showSalesChannelStructureFlag;

  /**
   * hideStoresPage2 FF defaults to true. To show stores page, add the appId to the FF in Split.
   *
   * e.g the api says hide the stores page but we want to show it, then add the appId to the FF in Split.
   * When true, Stores is omitted from the nav entirely (hard-omit) — Show hidden features does not override.
   */
  const hideStoresPage2 = useFlag('hideStoresPage2');
  const omitStoresFromNav = isApiFlagOn && hideStoresPage2;

  const showPayGreen = useFlag('paygreenFeatures');
  const showSmsCampaigns = useFlag('showSmsCampaigns');
  const showVerifactuFlag = useFlag('showVerifactuLink');
  const showGoogleListingManagement = useFlag('showGoogleListingManagement');
  const disablePropertyManagerLevelAddOns = useFlag('disablePropertyManagerLevelAddOns');
  const enableNewWebhooksSystem = useFlag('enableNewWebhooksSystem');
  const showAuthMenuItem = useFlag('showAuthMenuItem' as FrontEndFlag);
  const enableClientReportingPayoutsMfe = useFlag('enableClientReportingPayoutsMfe');
  const disableLegacyTeammate = useFlag('disableLegacyTeammate');
  const foodSafetyAddOnPermissions = useFlag('foodSafetyAddOnPermissions');
  const isMenuListingV3On = useFlag('showMenuListingV3');
  const isMenuListingV3Ready = useAppSelector((s) =>
    flagService.isFlagReady(s, 'showMenuListingV3')
  );

  /**
   * When false, Entitlements V2 is omitted from the nav entirely (hard-omit) —
   * Show hidden features does not override.
   */
  const omitEntitlementsV2FromNav = !useFeatureFlagValue(
    PortalFeatureFlags.accessEntitlementsV2Page,
    false
  );

  /**
   * When false, AI Marketing is omitted from the nav entirely (hard-omit) —
   * Show hidden features does not override. Mirrors the Harness flag that gates
   * the managed-marketing-service MFE, so the link is only shown where the MFE
   * is actually served (off in production until the MFE is available there).
   */
  const omitAiMarketingFromNav = !useFeatureFlagValue(PortalFeatureFlags.aiMarketing, false);
  const isPhoneAgentCutoverOn = useFeatureFlagValue(PortalFeatureFlags.phoneAgentCutover, false);

  /**
   * When false, Third Party Delivery is omitted from the nav entirely (hard-omit) —
   * Show hidden features does not override.
   */
  const omitThirdPartyDeliveryFromNav = !useFeatureFlagValue(
    PortalFeatureFlags.DOS_showThirdPartyDeliveryInSidebar,
    false
  );

  /**
   * Org-keyed backend flag (Split traffic type org_id). Wait until the provider
   * is ready before showing Sales or Orders. On / FLAG_NOT_FOUND → Sales,
   * off → Orders; not ready → omit both. flipdish-global always Sales.
   * forceSalesMenuOption ON short-circuits to Sales.
   */
  const salesOrdersNavChoice = getSalesOrdersNavChoice(
    currentAppId,
    useBooleanFeatureFlagResolution(
      PortalFeatureFlags.backend_menu_shouldAssignMenuToStoreFromOomEvents,
      false
    ),
    useFeatureFlagValue(PortalFeatureFlags.forceSalesMenuOption, false)
  );

  const addOnPermissions: AppState['permissions'] = disablePropertyManagerLevelAddOns
    ? franchisorPermissions
    : foodSafetyAddOnPermissions
      ? [...permissionsIncludingPropertyOwner, ...propertyManagerRolePermission]
      : permissionsIncludingPropertyOwner;

  const showMenuEditor = shouldShowRMSMenuEditor(menuSource);
  const useAuthZForGetApps = useBooleanFeatureFlagResolution(
    PortalFeatureFlags.backend_auth_useAuthZForGetApps,
    false
  );
  const useAuthZPermissions = useAuthZForGetApps.status === 'resolved' && useAuthZForGetApps.value;
  const propertiesPermissions = getPropertiesPermissions(useAuthZPermissions);
  const salesChannelPermissions = getSalesChannelPermissions(useAuthZPermissions);

  const menuListingPath =
    getMenuListingPath(
      currentAppId ?? '',
      isMenuListingV3On,
      isMenuListingV3Ready,
      isOrgListingV3On,
      isOrgListingV3Ready
    ) ?? '';

  const menuNavItem = getMenuNavItem(
    currentAppId ?? '',
    showMenuEditor,
    isRestrictedOnboardingNav,
    menuListingPath,
    useAuthZPermissions,
    showMenuSectionNav
  );

  const paygreenCountries = ['FR', 'BE'];
  const smsCampaignsCountries = ['IE', 'GB'];
  const links: ItemMetaData[] = [
    {
      title: 'Home',
      path: `/${currentAppId}/home`,
      icon: <HomeLine />,
      viewId: PortalViewId.Home,
      searchAlias: ['Main dashboard', 'Overview', 'Quick View'],
      hidden: reducedOnboardingView, // visible for onboardingWaitingView
    },
    {
      title: 'Sales',
      path: `/${currentAppId}/sales`,
      icon: <ShoppingBag01 />,
      // Same visibility as former Orders: ViewOrders is enough (no BASE gate).
      permissions: useAuthZPermissions
        ? ['ViewOrders', 'FlipdishStaff']
        : ['ViewOrders', 'PropertyOwner', 'Owner', 'ManagedOwner', 'FlipdishStaff'],
      hidden: isRestrictedOnboardingNav,
      omit: salesOrdersNavChoice !== 'sales',
      isMFE: true,
      searchAlias: [
        'Online Sales',
        'POS sales',
        'Point of Sale Sales',
        'Sales reports',
        'Sales by product',
        'Orders',
        'Order Management',
        'Customer Orders',
        'Live orders view',
      ],
    },
    {
      title: 'Orders',
      path: `/${currentAppId}/orders`,
      icon: <ShoppingBag01 />,
      permissions: useAuthZPermissions
        ? ['ViewOrders', 'FlipdishStaff']
        : ['ViewOrders', 'PropertyOwner', 'Owner', 'ManagedOwner', 'FlipdishStaff'],
      hidden: isRestrictedOnboardingNav,
      omit: salesOrdersNavChoice !== 'orders',
      searchAlias: [
        'Order Management',
        'Customer Orders',
        'Live orders view',
        'Sales',
        'Online Sales',
      ],
    },
    {
      title: 'Properties',
      path: `/${currentAppId}/properties`,
      icon: <Building02 />,
      viewId: PortalViewId.PropertiesList,
      permissions: propertiesPermissions,
      hidden: !showSalesChannelStructure || isRestrictedOnboardingNav,
      searchAlias: [
        'Opening hours',
        'Collection hours',
        'Delivery hours',
        'General settings',
        'Operating configuration',
        'Restaurant layout',
        'Geo configuration',
        'Address overrides',
        'Float',
        'Localisation configuration',
        'Archive property',
        'Restaurants',
        'Address',
        'Property Address',
        'Takeaway',
        'Restaurants',
        'Franchises',
        'Locations',
      ],
    },
    menuNavItem,
    {
      title: 'Reports',
      path: `/${currentAppId}/reports/dashboards`,
      icon: <BarChartSquare01 />,
      searchAlias: [
        'Orders Report',
        'Business Reports',
        'Overview Report',
        'End of day report',
        'Performance Summary',
        'Business Review',
        'Ordered Items Report',
        'Tips Report',
        'Campaign Performance Report',
        'Customers Report',
        'Sales Report',
        'Inventory Report',
        'Costs Report',
        'Payroll Report',
      ],
      hidden: isRestrictedOnboardingNav,
    },
    {
      title: 'Catalog',
      path: '',
      icon: <BookOpen01 />,
      hidden: !isProductBasedMenusOn || isRestrictedOnboardingNav,
      subItems: [
        {
          title: 'Products',
          path: `/${currentAppId}/products`,
          hidden: !isProductBasedMenusOn,
          icon: <BookOpen01 />,
          isBeta: true,
        },
        {
          title: 'Modifier_Groups',
          path: `/${currentAppId}/modifier-groups`,
          hidden: !isProductBasedMenusOn,
          icon: <BookOpen01 />,
          isBeta: true,
        },
      ],
    },
    {
      title: 'Marketing',
      path: '',
      icon: <Announcement01 />,
      hidden: isRestrictedOnboardingNav,
      subItems: [
        {
          title: 'Customers',
          path: `/${currentAppId}/customers`,
          icon: <UserSquare />,
          permissions: ['ViewCustomerStatistics'],
          searchAlias: [
            'List of customers',
            'List of consumers',
            'Average order value',
            'Customer name',
            'Customer phone number',
            'Total order value customer',
          ],
        },
        {
          title: 'Vouchers',
          path: `/${currentAppId}/vouchers`,
          icon: <Tag03 />,
          permissions: ['ViewVouchers'],
          searchAlias: [
            'Retention voucher',
            'Customer discount',
            'Loyalty voucher',
            'Discount vouchers',
            'Deal codes',
            'Promo codes',
          ],
        },
        {
          title: 'Campaigns',
          path: `/${currentAppId}/campaigns`,
          icon: <Announcement01 />,
          permissions: ['ViewCampaignsConfigurations'],
          hidden: Features?.includes('disable-campaign'),
          searchAlias: [
            'Loyalty Campaign',
            'Retention Campaign',
            'Loyalty rewards',
            'Retention rewards',
          ],
        },
        {
          title: 'Push_Notifications',
          path: `/${currentAppId}/push_notifications`,
          icon: <Bell01 />,
          searchAlias: [
            'Schedule push notification',
            'Customer messages',
            'Promote voucher code',
            'Mobile messages',
          ],
        },
        {
          title: 'Flipdish_Phone_Marketing_beta',
          path: isPhoneAgentCutoverOn
            ? `/${currentAppId}/phone-agent/setup`
            : `/${currentAppId}/flipdish-phone-marketing/home`,
          icon: <PhoneCall02 />,
          permissions: ['FlipdishStaff'],
          isMFE: true,
          searchAlias: ['Setup Phone Marketing'],
        },
        {
          title: 'AI_Marketing',
          path: `/${currentAppId}/ai-marketing`,
          icon: <TrendUp01 />,
          permissions: useAuthZPermissions
            ? ['CreateCampaignsConfigurations', 'UpdateCampaignsConfigurations', 'FlipdishStaff']
            : ['FlipdishStaff', 'Owner', 'ManagedOwner'],
          isMFE: true,
          omit: omitAiMarketingFromNav,
          searchAlias: ['AI Marketing', 'Amplify subscription', 'Amplify plans'],
        },
        {
          title: 'SMS_campaigns',
          path: `/${currentAppId}/marketing/campaigns`,
          icon: <MessageTextSquare01 />,
          hidden:
            CountryId === 'GB'
              ? false
              : !showSmsCampaigns || !smsCampaignsCountries.includes(CountryId || ''),
          isMFE: true,
          searchAlias: [
            'SMS campaign sales revenue',
            'SMS orders from campaigns',
            'Edit SMS campaign',
            'Create SMS campaign',
            'Edit SMS campaign',
          ],
        },
        {
          title: 'Google_Listing_Management',
          path: `/${currentAppId}/marketing/listing-management/google`,
          icon: <GoogleChrome />,
          hidden: !showGoogleListingManagement,
          isMFE: true,
          searchAlias: [
            'Manage google business profile',
            'Link google business profile',
            'Unlink google business profile',
            'Google business profile',
          ],
        },
      ],
    },
    {
      title: 'Finance',
      path: '',
      icon: <BankNote02 />,
      // Widened on purpose so the Accounts and Payroll roles keep the group; the group list is the
      // gate, so a narrower list (see Sales_channels) hides the group even when a child would show.
      permissions: [
        'ViewPayouts',
        'ViewBankAccounts',
        'FlipdishStaff',
        ...accountsPermissions,
        ...payrollPermissions,
      ],
      hidden: isRestrictedOnboardingNav,
      subItems: [
        {
          title: 'Payouts',
          path: `/${currentAppId}/finance/payouts`,
          icon: <CoinsHand />,
          permissions: ['ViewPayouts', 'FlipdishStaff'],
          isMFE: enableClientReportingPayoutsMfe,
          searchAlias: [
            'Payment',
            'Payout report',
            'Disbursement',
            'Remittance',
            'Fund transfer',
            'Earnings report',
            'Bank transfer',
            'Money transfer',
            'Weekly payout',
            'Daily payout',
            'Payment reconciliation report',
            'Balance transfer',
            'Payout summary',
            'Income summary',
            'Merchant account payout',
          ],
        },
        {
          title: 'Bank_accounts',
          path: `/${currentAppId}/finance/bank-accounts`,
          icon: <Bank />,
          permissions: ['ViewBankAccounts', 'FlipdishStaff'],
          searchAlias: [
            'IBAN',
            'Payout bank account',
            'Payment processing bank account',
            'Edit VAT number',
            'Vat details',
            'Update company information',
            'Registered company number',
            'Company verification',
            'Update my identity documents',
            'Update company number',
            'Edit bank account',
            'Edit bank',
            'Banking details',
            'Edit company account details',
            'Remove bank account',
          ],
        },
        {
          title: 'Restaurant_vouchers',
          path: `/${currentAppId}/finance/restaurant-vouchers`,
          icon: <CreditCard02 />,
          permissions: useAuthZPermissions
            ? ['ViewVouchers']
            : ['Owner', ...propertyOwnerRolePermission, 'ManagedOwner', 'FinanceManger'],
          hidden:
            !hasBankAccountsAccess || !showPayGreen || !paygreenCountries.includes(CountryId || ''),
        },
        {
          title: 'Ledger',
          path: `/${currentAppId}/finance/ledger`,
          icon: <BookClosed />,
          permissions: ['ViewBankAccounts', 'FlipdishStaff'],
          hidden: !hasBankAccountsAccess || !Features?.includes('ledger-menu-item'),
        },
        {
          title: 'Verifactu',
          path: `/${currentAppId}/receipt/verifactu`,
          icon: <ShieldTick />,
          permissions: ['ViewBankAccounts', 'FlipdishStaff'],
          hidden:
            !hasBankAccountsAccess ||
            !shouldShowVerifactu({
              showVerifactuFlag,
              orgCountryCode,
              orgId,
              isProduction,
            }),
          isBeta: true,
          isMFE: true,
          searchAlias: ['Verifactu'],
        },
        {
          title: 'Accounts',
          path: `/${currentAppId}/add-ons/accounts`,
          icon: <Calculator />,
          hidden: !subscriptions.includes(RMSSubscriptions.ACCOUNTS),
          permissions: accountsPermissions,
          searchAlias: [
            'Bills',
            'Credit Accounts',
            'Credit Account Status',
            'Credit Books',
            'Expenses',
            'Transaction Accounts',
            'Account categories',
            'Book keeping',
          ],
        },
        {
          title: 'Payroll',
          path: `/${currentAppId}/add-ons/payroll`,
          icon: <Wallet01 />,
          permissions: payrollPermissions,
          searchAlias: [
            'Holiday planner',
            'Payroll calculator',
            'Edit payroll settings',
            'Edit payroll configuration',
            'Set payroll schedule',
            'Rota',
            'Manage rota',
          ],
        },
      ],
    },
    {
      title: 'Sales_channels',
      path: '',
      icon: <Monitor02 />,
      permissions: useAuthZPermissions
        ? ['ViewSalesChannels', 'EditSalesChannels', 'ViewHydraConfig']
        : [
            'ViewSalesChannels',
            'EditSalesChannels',
            'ViewHydraConfig',
            ...propertyManagerRolePermission,
            ...propertyOwnerRolePermission,
          ],
      hidden: isRestrictedOnboardingNav,
      subItems: [
        {
          title: 'Point_of_Sale',
          path: `/${currentAppId}/sales-channels/point-of-sale`,
          icon: <Tv01 />,
          permissions: salesChannelPermissions,
          hidden: !subscriptions.includes(RMSSubscriptions.BASE),
          searchAlias: [
            'POS Settings',
            'Point of Sale configuration',
            'Cash register setup',
            'POS configuration',
            'POS hardware setup',
            'POS hardware settings',
            'POS system settings',
          ],
        },
        {
          title: 'Mobile_apps',
          path: `/${currentAppId}/sales-channels/mobile-apps`,
          icon: <Phone02 />,
          permissions: salesChannelPermissions,
          searchAlias: [
            'Edit mobile app settings',
            'Edit mobile app sales channels',
            'Edit collection and delivery settings',
            'Mobile app device settings',
            'Change restaurants that appear in the app',
            'Change list of addresses in mobile app',
            'Mobile app configurations',
            'Opening hours',
            'Opening hour overrides',
          ],
        },
        {
          title: 'Website',
          path: `/${currentAppId}/sales-channels/website`,
          icon: <Globe01 />,
          permissions: salesChannelPermissions,
          searchAlias: [
            'Edit website settings',
            'Edit website sales channels',
            'Edit collection and delivery settings',
            'Online order settings',
            'Order settings',
            'Website device settings',
            'Change restaurants that appear in the website',
            'Change list of addresses in website',
            'Website editor',
            'Website configuration',
            'Domain setup',
            'Cookie management',
            'Website settings',
            'DNS settings',
            'Website builder',
            'Customize website',
            'Opening hours',
            'Opening hour overrides',
          ],
        },
        {
          title: 'Kiosks',
          path: `/${currentAppId}/sales-channels/kiosk`,
          icon: <Monitor01 />,
          permissions: salesChannelPermissions,
          searchAlias: [
            'Kiosk settings',
            'Kiosk list',
            'Kiosk sales channels',
            'Kiosk configuration',
            'Kiosk home screen customisation',
            'Edit kiosk home screen',
            'Pair kiosk',
            'Add kiosk',
            'Remove kiosk',
            'View kiosks',
            'Edit kiosk sales channels',
            'Configure kiosk',
            'Add table settings',
            'Opening hours',
            'Opening hour overrides',
          ],
        },
        {
          title: 'Phone_agent',
          path: `/${currentAppId}/sales-channels/phone-agent`,
          icon: <Phone02 />,
          permissions: salesChannelPermissions,
          searchAlias: [
            'Phone agent',
            'Phone agent sales channels',
            'AI phone agent',
            'Edit phone agent sales channels',
            'Phone agent configuration',
            'Opening hours',
            'Opening hour overrides',
          ],
          hidden: isLoadingEntitlement || !hasPhoneAgentEntitlement,
        },
        {
          title: 'Marketplaces',
          path: `/${currentAppId}/sales-channels/marketplaces`,
          icon: <ShoppingCart03 />,
          permissions: salesChannelPermissions,
          hidden: !showSalesChannelStructure,
          searchAlias: [
            'Marketplace sales channel',
            'Deliveroo',
            'Just Eat',
            'Uber Eats',
            'Add marketplace',
            'Edit marketplace opening hours',
            'Edit marketplace settings',
            'Marketplace configuration',
            'Marketplace devices',
            'Delivery charge marketplace',
            'Marketplace menu',
            'Timing configuration',
          ],
        },
      ],
    },
    {
      title: 'Stores',
      path: `/${currentAppId}/storegroups`,
      icon: <Building02 />,
      hidden: isRestrictedOnboardingNav,
      omit: omitStoresFromNav,
    },
    {
      title: 'First_Time_Setup',
      path: `/${currentAppId}/setup`,
      icon: <Rocket02 />,
      omit: !shouldShowFirstTimeSetup,
      permissions: ['ViewOnboarding', 'FlipdishStaff'],
      isMFE: true,
    },
    {
      title: 'developer_tools',
      section: 'footer',
      path: '',
      icon: <TerminalSquare />,
      // No group permissions: the account-menu link this replaces had none.
      hidden: isRestrictedOnboardingNav,
      subItems: [
        {
          title: 'Webhooks',
          path: `/${currentAppId}/settings/webhooks`,
          icon: <Dataflow02 />,
          permissions: ['ViewDevelopersSettings', 'EditDevelopersSettings'],
          hidden: !enableNewWebhooksSystem,
          isMFE: true,
          searchAlias: ['Webhook configuration', 'Webhook settings', 'Webhook management'],
        },
        {
          title: 'Oauth_apps_link',
          path: `/${currentAppId}/developers/apps`,
          icon: <Fingerprint02 />,
          searchAlias: ['OAuth apps', 'API keys', 'Developer tools'],
        },
        {
          title: 'App_store_apps',
          path: `/${currentAppId}/appstore/developers`,
          icon: <Data />,
          searchAlias: ['App store apps', 'Developer apps'],
        },
        {
          title: 'Metafields',
          path: `/${currentAppId}/settings/metafields`,
          icon: <Code02 />,
          hidden: !isProductBasedMenusOn,
        },
      ],
    },
    {
      title: 'Settings',
      section: 'footer',
      path: '',
      icon: <Settings01 />,
      // Widened on purpose so the Staff roles keep the group; the group list is the gate, so a
      // narrower list (see Sales_channels) hides the group even when a child would show.
      permissions: useAuthZPermissions
        ? [
            'ViewWebsiteContent',
            'ViewTeammates',
            'FlipdishStaff',
            'ViewPayrollStaff',
            'EditPayrollStaff',
          ]
        : [
            'Owner',
            'ManagedOwner',
            ...propertyOwnerRolePermission,
            'ViewTeammates',
            'ViewPayrollStaff',
            'EditPayrollStaff',
          ],
      hidden: isRestrictedOnboardingNav,
      subItems: [
        {
          title: 'Organization',
          path: `/${currentAppId}/settings/organisation`,
          icon: <Building07 />,
          permissions: useAuthZPermissions
            ? (['ViewBrand', 'FlipdishStaff'] as AppState['permissions'])
            : ['Owner', 'ManagedOwner', 'FlipdishStaff'],
          searchAlias: ['Organisation settings'],
        },
        {
          title: 'Brand',
          path: `/${currentAppId}/settings/brands`,
          icon: <BookmarkCheck />,
          permissions: useAuthZPermissions
            ? (['ViewBrand', 'FlipdishStaff'] as AppState['permissions'])
            : ['Owner', 'ManagedOwner', 'FlipdishStaff'],
          searchAlias: ['Brand settings'],
        },
        {
          title: 'Teammates',
          path: `/${currentAppId}/teammates`,
          icon: <Users01 />,
          permissions: ['EditTeammates', 'ViewTeammates'],
          searchAlias: ['Add users', 'Remove users', 'Add teammates', 'Remove teammates'],
          hidden: disableLegacyTeammate,
        },
        {
          title: 'Users',
          path: `/${currentAppId}/settings/teammates`,
          icon: <Users01 />,
          permissions: ['ViewTeammates'],
          isMFE: true,
          hidden: !showAuthMenuItem,
          searchAlias: [
            'Teammate Permissions',
            'Permissions',
            'Authorization',
            'Auth',
            'Access',
            'Users',
            'User Permissions',
          ],
        },
        {
          title: 'Staff',
          path: `/${currentAppId}/add-ons/staff`,
          icon: <UsersEdit />,
          permissions: ['ViewPayrollStaff', 'EditPayrollStaff'],
          searchAlias: [
            'Add staff',
            'Manage staff',
            'Define staff roles',
            'Staff permissions',
            'Set staff pin',
            'Set POS pin',
            'Clock in settings',
            'Add kitchen staff',
            'Add POS staff',
            'Add POS users',
            'Add driver to staff',
            'Add staff roles',
            'View staff roles',
          ],
        },
        {
          title: 'Sales_channels',
          path: `/${currentAppId}/settings/sales-channels`,
          icon: <Monitor02 />,
          permissions: ['ViewSalesChannels', 'EditSalesChannels'],
          searchAlias: ['Sales channel settings'],
        },
        {
          title: 'Sales_channel_groups',
          path: `/${currentAppId}/settings/sales-channel-groups`,
          icon: <Rows01 />,
          permissions: ['ViewSalesChannels', 'EditSalesChannels'],
          hidden: !showSalesChannelStructure,
          searchAlias: ['Sales channel groups'],
        },
        {
          title: 'Customer_messages',
          path: `/${currentAppId}/settings/customer-messages/`,
          icon: <Inbox01 />,
          permissions: useAuthZPermissions
            ? ['ViewWebsiteContent', 'FlipdishStaff']
            : ['Owner', 'ManagedOwner', 'FlipdishStaff'],
          hidden: !showSalesChannelStructure,
        },
        {
          title: 'Entitlements',
          path: `/${currentAppId}/entitlements`,
          icon: <CheckVerified03 />,
          permissions: ['FlipdishStaff', 'ShowHiddenFeatures'],
        },
        {
          title: 'Entitlements_V2',
          path: `/${currentAppId}/entitlements-v2`,
          icon: <CheckVerified03 />,
          permissions: ['FlipdishStaff'],
          isBeta: true,
          isMFE: true,
          omit: omitEntitlementsV2FromNav,
        },
        {
          title: 'Audit_logs',
          path: `/${currentAppId}/logs`,
          icon: <List />,
          permissions: [
            'ViewAppAuditLogs',
            'ViewBankAccountAuditLogs',
            'ViewCampaignsAuditLogs',
            'ViewCustomerAuditLogs',
            'ViewFeeConfigurationsAuditLogs',
            'ViewHydraAuditLogs',
            'ViewMenuAuditLogs',
            'ViewOrdersAuditLogs',
            'ViewPrinterAuditLogs',
            'ViewPushNotificationAuditLogs',
            'ViewStoreAuditLogs',
            'ViewTeammatesAuditLogs',
            'ViewUserEventsAuditLogs',
            'ViewVouchersAuditLogs',
            'ViewApmAuditLogs',
          ],
          searchAlias: ['history'],
        },
        {
          title: 'Third_party_delivery',
          path: `/${currentAppId}/third-party-delivery`,
          icon: <Car01 />,
          viewId: PortalViewId.SettingsThirdPartyDelivery,
          permissions: useAuthZPermissions
            ? ['ViewWebsiteContent', 'FlipdishStaff']
            : ['Owner', 'ManagedOwner', 'FlipdishStaff'],
          isMFE: true,
          omit: omitThirdPartyDeliveryFromNav,
          searchAlias: [
            'Delivery integrations',
            'Delivery partners',
            'Dispatch',
            'JET Go',
            'Uber Direct',
            'Third party delivery',
            'Delivery fee',
          ],
        },
      ],
    },
    {
      title: 'Add_ons',
      path: '',
      icon: <PlusCircle />,
      hidden: isRestrictedOnboardingNav,
      subItems: [
        {
          title: 'App_Store',
          path: `/${currentAppId}/appstore`,
          icon: <Grid01 />,
          permissions: useAuthZPermissions ? ['UpdateApp', 'FlipdishStaff'] : ['Owner'],
          searchAlias: [
            'Google Ordering',
            'Star Micronics cloud print',
            'Uber Direct configuration',
            'Customer Feedback App',
            'Flipdish POS',
            'Glovo Delivery',
          ],
        },
        {
          title: 'Documents',
          path: `/${currentAppId}/add-ons/documents`,
          icon: <File06 />,
          hidden: !subscriptions.includes(RMSSubscriptions.DOCUMENTS),
          permissions: ['EditDocumentExplorer', 'ViewDocumentExplorer'],
        },
        {
          title: 'FoodSafety',
          path: `/${currentAppId}/add-ons/food-safety`,
          icon: <Award03 />,
          hidden: !subscriptions.includes(RMSSubscriptions.FOOD_SAFETY),
          permissions: useAuthZPermissions
            ? ['ViewDocumentExplorer', 'FlipdishStaff']
            : addOnPermissions,
          searchAlias: [
            'Appliances',
            'Temperature logs',
            'Incident logs',
            'Incident types',
            'Stock item cooking temperature logs',
            'Stock item storage temperature logs',
            'Food safety tasks',
            'Food safety types',
            'Task Logs',
          ],
        },
        {
          title: 'Inventory',
          path: `/${currentAppId}/add-ons/inventory`,
          icon: <Dotpoints01 />,
          hidden: !subscriptions.includes(RMSSubscriptions.INVENTORY),
          permissions: inventoryPermissions,
          searchAlias: [
            'Stock',
            'Stock items',
            'Stock item categories',
            'Stock item suppliers',
            'Tracking profiles',
            'Purchase orders',
            'Manage purchase orders',
            'Edit stock items',
            'Suppliers',
          ],
        },
        {
          title: 'Signage',
          path: `/${currentAppId}/add-ons/signage`,
          icon: <Stand />,
          hidden: !subscriptions.includes(RMSSubscriptions.SIGNAGE),
          permissions: useAuthZPermissions
            ? ['ViewWebsiteContent', 'FlipdishStaff']
            : ['Owner', 'ManagedOwner', 'FlipdishStaff'],
          searchAlias: [
            'Edit signage',
            'Add signage',
            'Manage signage',
            'Customer display',
            'Active order signage',
            'Signage slide',
            'New signage player',
            'Customer display screen signage',
          ],
        },
        {
          title: 'Central_kitchen',
          path: `/${currentAppId}/add-ons/central-kitchen`,
          icon: <Building05 />,
          hidden: !subscriptions.includes(RMSSubscriptions.CENTRAL_KITCHEN),
          permissions: useAuthZPermissions
            ? ['ViewWebsiteContent', 'FlipdishStaff']
            : ['Owner', 'ManagedOwner', 'FlipdishStaff'],
          searchAlias: [
            'Settings central kitchen',
            'Configuration central kitchen',
            'Manage orders central kitchen',
            'Central kitchen service',
          ],
        },
        {
          title: 'Terminals',
          path: `/${currentAppId}/terminals`,
          icon: <Tablet02 />,
          permissions: ['ViewHydraConfig'],
          searchAlias: ['Add terminal', 'Delete terminal', 'View terminal'],
        },
      ],
    },
    {
      title: 'Flipdish_Staff_Tools',
      section: 'footer',
      toggle: 'showHiddenFeatures',
      path: '',
      icon: <Tool02 />,
      permissions: ['FlipdishStaff'],
      hidden: isRestrictedOnboardingNav,
      subItems: [
        {
          title: 'Bulk_Settings_Updater',
          path: `/${currentAppId}/bulk-updater/bulk-fee-updater`,
          icon: <Dataflow02 />,
          permissions: ['FlipdishStaff'],
          isMFE: true,
        },
        {
          title: 'Advanced_Restaurant_Search',
          path: `/${currentAppId}/bulk-updater/restaurant-search`,
          icon: <SearchSm />,
          permissions: ['FlipdishStaff'],
          isMFE: true,
        },
        {
          title: 'Advanced_User_Search',
          path: `/${currentAppId}/bulk-updater/user-search`,
          icon: <UsersEdit />,
          permissions: ['FlipdishStaff'],
          isMFE: true,
        },
        {
          title: 'Org_View',
          path: `/${currentAppId}/bulk-updater/org-view`,
          icon: <Building07 />,
          permissions: ['FlipdishStaff'],
          isMFE: true,
        },
        {
          title: 'System_settings_for_all_orgs',
          path: `/${currentAppId}/bulk-updater/system`,
          icon: <Settings01 />,
          permissions: ['FlipdishStaff'],
          isMFE: true,
        },
        {
          title: 'Logo_Resizer',
          path: `/${currentAppId}/bulk-updater/logo-resizer`,
          icon: <ImageCheck />,
          permissions: ['FlipdishStaff'],
          isMFE: true,
        },
        {
          title: 'Onboarding_Configuration_Tool',
          path: `/${currentAppId}/bulk-updater/onboarding-configuration`,
          icon: <Rocket02 />,
          permissions: ['FlipdishStaff'],
          isMFE: true,
        },
        {
          title: 'Mobile_Apps_Tools',
          path: `/${currentAppId}/mobile-apps-tools`,
          icon: <Phone02 />,
          permissions: ['FlipdishStaff'],
          isMFE: true,
        },
        {
          title: 'Website_domain_manager',
          path: `/${currentAppId}/website-domain-manager`,
          icon: <Globe01 />,
          permissions: ['FlipdishStaff'],
          isMFE: true,
        },
        {
          title: 'Cross_sell_AI_migration',
          path: `/${currentAppId}/admin/cross-sell-migrations`,
          icon: <Stars01 />,
          permissions: ['FlipdishStaff'],
          isMFE: true,
        },
        {
          title: 'Gloria_Food',
          path: `/${currentAppId}/bulk-updater/gloria-food`,
          icon: <Dataflow03 />,
          permissions: ['FlipdishStaff'],
          isMFE: true,
        },
        {
          title: 'Fraud_control',
          path: `/${currentAppId}/finance/admin/fraud-control/history`,
          icon: <Lock01 />,
          permissions: ['FlipdishStaff'],
          isMFE: true,
        },
        {
          title: 'SMS_senders',
          path: `/${currentAppId}/admin/sms-senders`,
          icon: <MessageTextSquare01 />,
          permissions: ['FlipdishStaff'],
          isMFE: true,
        },
      ],
    },
  ];

  return removeOmittedMenuItems(links);
};
